import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'

/**
 * Runtime configuration interface.
 * It contains all the properties that are loaded from the config.json file and
 * that could change depending on the env where the application is deployed.
 */
export interface RuntimeConfig {
   environment: string
   apiUrl: string
   mediaUrl: string
   pmpDetailUrl: string
}

/**
 * Service that provides the runtime configuration of the application.
 * It loads the configuration from a remote endpoint and provides it to the application.
 */
@Injectable({
   providedIn: 'root',
})
export class ConfigService {
   private readonly RUNTIME_CONFIG_PATH = './assets/configs/properties.json'

   private config: RuntimeConfig = {
      environment: 'NONE',
      apiUrl: '',
      mediaUrl: '',
      pmpDetailUrl: '',
   }

   constructor(private readonly httpClient: HttpClient) {}

   /**
    * Initializes the dynamic configuration from the config.json file.
    * @returns {Promise<RuntimeConfig>} The dynamic configuration.
    */
   initRuntimeConfiguration(): Promise<RuntimeConfig> {
      return this.httpClient
         .get<RuntimeConfig>(`${this.RUNTIME_CONFIG_PATH}?ts=${Date.now()}`)
         .toPromise()
         .then((dc) => {
            this.config = dc
            console.log('Runtime Configuration loaded for env: ', this.environment)
            return dc
         })
   }

   /**
    * @returns {string} The current environment from the dynamic configuration. Expected values are NONE | LOCAL | DEV | TEST | PROD
    */
   get environment(): string {
      return this.config.environment
   }

   /**
    * Gets the API URL from the runtime configuration.
    *
    * @return {string} The API URL as specified in the runtime configuration.
    */
   get apiUrl(): string {
      return this.config.apiUrl
   }

   get mediaUrl(): string {
      return this.config.mediaUrl
   }

   get pmpDetailUrl(): string {
      return this.config.pmpDetailUrl
   }
}
